import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API, useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Plus, Search, ChevronRight, FolderKanban, X, Loader2, Download, Trash2 } from "lucide-react";

// N12 (roadmap-valoriza-jev-2026-10-11, Ola 4) · "completar filtros y
// mostrar nombres canónicos legibles" — antes esta lista era fija
// (dotnet/gis/otro) mientras los datos reales incluían también
// commercial/development/hardware/integration/movilidad (184 de ~227
// proyectos reales quedaban invisibles al filtro, solo alcanzables vía
// "Todos"). Ahora las opciones se calculan de los datos reales cargados,
// con una etiqueta legible cuando existe, y el valor técnico como
// respaldo si aparece un tipo nuevo no mapeado todavía.
const TYPE_LABELS = {
  dotnet: ".NET", gis: "GIS", otro: "Otro", commercial: "Comercial",
  development: "Desarrollo", hardware: "Hardware", integration: "Integración",
  movilidad: "Movilidad",
};
const STATUS_LABELS_FILTER = {
  active: "Activo", paused: "Pausado", completed: "Completado",
  blocked: "Bloqueado", planning: "Planificación",
};
const STATUSES = ["Todos", "active", "paused", "planning", "completed", "blocked"];
const PRIORITIES = ["Todos", "critica", "alta", "media", "baja"];

const statusLabel = { active: "Activo", paused: "Pausado", completed: "Completado", blocked: "Bloqueado" };
const statusColor = { active: "status-active", paused: "status-paused", completed: "status-completed", blocked: "status-blocked" };
const priorityColor = { critica: "priority-critica", alta: "priority-alta", media: "priority-media", baja: "priority-baja" };

export default function Projects() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("Todos");
  const [filterStatus, setFilterStatus] = useState("Todos");
  const [filterPriority, setFilterPriority] = useState("Todos");
  const [showModal, setShowModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [form, setForm] = useState({ title: "", type: "dotnet", priority: "media", area: "", responsible_tech: "", estimated_days: 0, description: "", end_date: "", budget_allocated: 0 });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/projects`);
      setProjects(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // N12 · opciones de tipo derivadas de los datos reales cargados, nunca
  // una lista fija que pueda dejar proyectos reales fuera del filtro.
  const TYPES = useMemo(() => ["Todos", ...new Set(projects.map(p => p.type).filter(Boolean))], [projects]);

  const activeFilters = [search, filterType !== "Todos", filterStatus !== "Todos", filterPriority !== "Todos"].filter(Boolean).length;

  const filtered = projects.filter(p => {
    const matchSearch = !search || p.title.toLowerCase().includes(search.toLowerCase()) || p.area?.toLowerCase().includes(search.toLowerCase()) || p.responsible_tech?.toLowerCase().includes(search.toLowerCase());
    const matchType = filterType === "Todos" || p.type === filterType;
    const matchStatus = filterStatus === "Todos" || p.status === filterStatus;
    const matchPriority = filterPriority === "Todos" || p.priority === filterPriority;
    return matchSearch && matchType && matchStatus && matchPriority;
  });

  const exportExcel = () => {
    const rows = [["Título", "Tipo", "Estado", "Prioridad", "Resp. Técnico", "Progreso %", "Presupuesto €", "Fecha Límite"]];
    filtered.forEach(p => rows.push([p.title, p.type, p.status, p.priority, p.responsible_tech || "", p.progress || 0, p.budget || p.budget_allocated || "", p.end_date || ""]));
    const csv = rows.map(r => r.map(c => `"${c}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "proyectos.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const handleCreate = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await axios.post(`${API}/projects`, form);
      setShowModal(false);
      setForm({ title: "", type: "dotnet", priority: "media", area: "", responsible_tech: "", estimated_days: 0, description: "", end_date: "", budget_allocated: 0 });
      load();
    } catch (err) { alert(err.response?.data?.detail || "Error al crear proyecto"); }
    finally { setSaving(false); }
  };

  const handle = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleDelete = async (e, projectId, projectTitle) => {
    e.stopPropagation();
    if (!window.confirm(`¿Eliminar el proyecto "${projectTitle}" y todos sus work items?\n\nEsta acción no se puede deshacer.`)) return;
    setDeletingId(projectId);
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      setProjects(prev => prev.filter(p => p.id !== projectId));
    } catch (err) {
      alert(err.response?.data?.detail || "Error al eliminar el proyecto");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">{t("projects.title")}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t("projects.subtitle")} · {projects.length} · {filtered.length}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportExcel} className="flex items-center gap-2 border border-gray-200 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-all">
            <Download size={14} /> {t("ofertasComercial.exportExcel")}
          </button>
          <button onClick={() => setShowModal(true)} data-testid="create-project-btn"
            className="flex items-center gap-2 bg-[#007934] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#006528] transition-all active:scale-95 shadow-sm">
            <Plus size={16} /> {t("projects.new")}
          </button>
        </div>
      </div>

      {/* Iter313 · O01 — aclarar que este catálogo (sincronizado con Azure
          DevOps) es independiente del de "Proyectos" en Gestión de Horas
          (fases comerciales de licitación). No deben fusionarse. */}
      <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-xs text-blue-800" data-testid="projects-scope-note">
        <FolderKanban size={14} className="flex-shrink-0 mt-0.5" />
        <span>
          Catálogo de <b>Proyectos IT</b> sincronizado con Azure DevOps ({projects.length} proyectos).
          Es independiente del catálogo de <b>Proyectos</b> en Gestión de Horas (fases comerciales para imputación de jornada) — poblaciones distintas por diseño, no es un error de sincronización.
        </span>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-48">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar proyectos..."
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#007934] focus:ring-2 focus:ring-[#007934]/20"
              data-testid="project-search" />
          </div>
          {[["Tipo", TYPES, filterType, setFilterType, TYPE_LABELS], ["Estado", STATUSES, filterStatus, setFilterStatus, STATUS_LABELS_FILTER], ["Prioridad", PRIORITIES, filterPriority, setFilterPriority, null]].map(([label, opts, val, setter, labels]) => (
            <select key={label} value={val} onChange={e => setter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] bg-white text-gray-700"
              data-testid={`filter-${label.toLowerCase()}`}>
              {opts.map(o => <option key={o} value={o}>{o === "Todos" ? "Todos" : (labels?.[o] || o)}</option>)}
            </select>
          ))}
          {(search || filterType !== "Todos" || filterStatus !== "Todos" || filterPriority !== "Todos") && (
            <button onClick={() => { setSearch(""); setFilterType("Todos"); setFilterStatus("Todos"); setFilterPriority("Todos"); }}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 px-2 py-2">
              <X size={14} /> Limpiar {activeFilters > 0 && <span className="bg-[#007934] text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center ml-0.5">{activeFilters}</span>}
            </button>
          )}
        </div>
      </div>

      {/* Projects table */}
      {loading ? (
        <div className="grid grid-cols-1 gap-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-20 rounded-xl loading-shimmer" />)}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden" data-testid="projects-table">
          {filtered.length === 0 ? (
            <div className="text-center py-16">
              <FolderKanban size={40} className="text-gray-200 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No hay proyectos</p>
              <p className="text-gray-400 text-sm">Crea tu primer proyecto con el botón de arriba</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    {["Proyecto", "Tipo", "Estado", "Prioridad", "Resp. Técnico", "Progreso", "Presupuesto", ""].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map(p => (
                    <tr key={p.id} className="group hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/projects/${p.id}`)} data-testid={`project-row-${p.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-[#E8F5E9] rounded-lg flex items-center justify-center flex-shrink-0">
                            <FolderKanban size={14} className="text-[#007934]" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{p.title}</p>
                            <p className="text-xs text-gray-400">{p.area}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium uppercase">{TYPE_LABELS[p.type] || p.type}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor[p.status] || "neutral"}`}>{statusLabel[p.status] || p.status}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${priorityColor[p.priority] || ""}`}>{p.priority}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{p.responsible_tech || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2" title={`${p.progress || 0}% completado`}>
                          <div className="w-16 h-1.5 bg-gray-100 rounded-full">
                            <div className="h-full bg-[#007934] rounded-full transition-all" style={{ width: `${p.progress || 0}%` }} />
                          </div>
                          <span className="text-xs text-gray-500">{p.progress || 0}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {(p.budget || p.budget_allocated) ? `${(p.budget || p.budget_allocated).toLocaleString("es-ES")}€` : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {isAdmin && (
                            <button
                              onClick={e => handleDelete(e, p.id, p.title)}
                              disabled={deletingId === p.id}
                              data-testid={`delete-project-${p.id}`}
                              title="Eliminar proyecto"
                              className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all disabled:opacity-30"
                            >
                              {deletingId === p.id
                                ? <Loader2 size={14} className="animate-spin text-red-500" />
                                : <Trash2 size={14} />}
                            </button>
                          )}
                          <ChevronRight size={16} className="text-gray-300" />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl animate-fade-up" data-testid="create-project-modal">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900 font-['Outfit']">Nuevo Proyecto</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleCreate} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Título *</label>
                  <input name="title" value={form.title} onChange={handle} required placeholder="Nombre del proyecto"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="project-title-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Tipo</label>
                  <select name="type" value={form.type} onChange={handle} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="project-type-select">
                    <option value="dotnet">.NET</option>
                    <option value="gis">GIS</option>
                    <option value="development">Desarrollo</option>
                    <option value="integration">Integración</option>
                    <option value="hardware">Hardware</option>
                    <option value="movilidad">Movilidad</option>
                    <option value="commercial">Comercial</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Prioridad</label>
                  <select name="priority" value={form.priority} onChange={handle} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="project-priority-select">
                    <option value="critica">Crítica</option>
                    <option value="alta">Alta</option>
                    <option value="media">Media</option>
                    <option value="baja">Baja</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Área</label>
                  <input name="area" value={form.area} onChange={handle} placeholder="Ej: Movilidad, GIS..."
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Responsable Técnico</label>
                  <input name="responsible_tech" value={form.responsible_tech} onChange={handle}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Estimación (días)</label>
                  <input name="estimated_days" type="number" value={form.estimated_days} onChange={handle}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Presupuesto (€)</label>
                  <input name="budget_allocated" type="number" value={form.budget_allocated} onChange={handle}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Fecha límite</label>
                  <input name="end_date" type="date" value={form.end_date} onChange={handle}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Descripción</label>
                  <textarea name="description" value={form.description} onChange={handle} rows={3}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] resize-none" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 border border-gray-200 text-gray-700 rounded-lg py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={saving} className="flex-1 bg-[#007934] text-white rounded-lg py-2.5 text-sm font-medium hover:bg-[#006528] disabled:opacity-60 flex items-center justify-center gap-2" data-testid="save-project-btn">
                  {saving ? <><Loader2 size={14} className="animate-spin" /> Guardando...</> : "Crear proyecto"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
